import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAccountStore } from '@/store/use-account-store';
import { useOrdersStore } from '@/store/use-orders-store';
import { getErrorMsg, OrderStatus } from '@/types';
import { cn } from '@/utils/cn';
import { formatOrderStatus } from '@/utils/value-format';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

type Props = {
  orderId: string;
};

export default function Status({ orderId }: Props) {
  const { t } = useTranslation();
  const address = useAccountStore((state) => state.address);
  const { status, code } = useOrdersStore(
    useShallow((state) => {
      const order = state.getOrders(address).orders[orderId];
      if (order.status === OrderStatus.REJECTED) {
        return {
          status: order.status,
          code: order.code,
        };
      }
      return { status: order.status };
    }),
  );
  return (
    <div
      className={cn(
        'flex items-center gap-2',
        status === OrderStatus.FILLED && 'text-green',
        status === OrderStatus.CANCELLED && 'text-red',
        status === OrderStatus.REJECTED && 'text-red',
      )}
    >
      {status ? formatOrderStatus(status, t) : '-'}
      {status === OrderStatus.REJECTED && code && (
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger>
              <Info size={14} />
            </TooltipTrigger>
            <TooltipContent>{getErrorMsg(code, t)}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

export const statusAccessorFn = (id: string) => {
  const { address } = useAccountStore.getState();
  const { status } = useOrdersStore.getState().getOrders(address).orders[id];
  return status;
};
